import { apiClient } from "../../config/api.config";

/**
 * Get all Aspero webhook logs with pagination and filtering
 * @param {Object} params - Query parameters
 * @param {number} [params.page=1] - Page number
 * @param {number} [params.limit=10] - Results per page (max: 100)
 * @param {string} [params.sort='createdAt'] - Sort field
 * @param {string} [params.order='desc'] - Sort order (asc/desc)
 * @param {string} [params.eventName] - Filter by event name
 * @param {string} [params.clientUuid] - Filter by client UUID
 * @param {string} [params.search] - Search in userName, mobileNumber, eventName
 * @param {string} [params.startDate] - Filter from date (ISO format)
 * @param {string} [params.endDate] - Filter to date (ISO format)
 * @returns {Promise<{data: {success: boolean, data: Array, meta: Object}}>}
 */
export const getAsperoLogs = (params) => {
  return apiClient.get("/admin/aspero/aspero-logs", { params });
};

/**
 * Get webhook logs for a specific user by client UUID
 * @param {string} clientUuid - User's client UUID
 * @param {Object} [params] - Optional query parameters (page, limit, eventName, startDate, endDate)
 * @returns {Promise<{data: {success: boolean, data: Array, meta: Object}}>}
 */
export const getAsperoLogsByUser = (clientUuid, params = {}) => {
  return apiClient.get(`/admin/aspero/aspero-logs/user/${clientUuid}`, { params });
};

/**
 * Get webhook logs for a specific event type
 * @param {string} eventName - Event type name (e.g., 'KYC_COMPLETED', 'PAYMENT_SUCCESS')
 * @param {Object} [params] - Optional query parameters (page, limit)
 * @returns {Promise<{data: {success: boolean, data: Array, meta: Object}}>}
 */
export const getAsperoLogsByEvent = (eventName, params = {}) => {
  return apiClient.get(`/admin/aspero/aspero-logs/event/${eventName}`, { params });
};

/**
 * Get detailed information for a single webhook log
 * @param {string} logId - MongoDB ObjectId (24 hex characters)
 * @returns {Promise<{data: {success: boolean, data: Object}}>}
 */
export const getAsperoLogDetails = (logId) => {
  return apiClient.get(`/admin/aspero/aspero-logs/${logId}`);
};

/**
 * Get webhook statistics including event distribution and daily trends
 * @param {Object} [params] - Optional query parameters
 * @param {string} [params.startDate] - Filter from date (ISO format)
 * @param {string} [params.endDate] - Filter to date (ISO format)
 * @returns {Promise<{data: {success: boolean, data: {totalWebhooks: number, eventDistribution: Array, dailyStats: Array}}}>}
 */
export const getAsperoLogsStats = (params = {}) => {
  return apiClient.get("/admin/aspero/aspero-logs-stats", { params });
};

/**
 * Get system health metrics for webhook monitoring
 * @returns {Promise<{data: {success: boolean, data: {status: string, metrics: Object, timestamp: string, error?: string}}}>}
 * @example
 * // Healthy response:
 * {
 *   success: true,
 *   data: {
 *     status: 'healthy',
 *     metrics: {
 *       totalEvents: 1523,
 *       eventsLastHour: 45,
 *       invalidEvents: 3,
 *       invalidPercentage: 0.2
 *     },
 *     timestamp: '2025-01-23T12:30:00.000Z'
 *   }
 * }
 */
export const getAsperoHealth = () => {
  return apiClient.get("/admin/aspero/aspero-health");
};

/**
 * Get storage statistics including database size and age distribution
 * @returns {Promise<{data: {success: boolean, data: {storage: Object, ageDistribution: Array}}}>}
 * @example
 * // Response:
 * {
 *   success: true,
 *   data: {
 *     storage: {
 *       totalDocuments: 1523,
 *       storageSize: 2457600,
 *       storageSizeMB: '2.34',
 *       avgDocumentSize: 1613,
 *       indexSize: 163840,
 *       indexSizeMB: '0.16',
 *       totalSizeMB: '2.50'
 *     },
 *     ageDistribution: [
 *       { label: 'Last 24 hours', count: 89 },
 *       { label: 'Last 7 days', count: 456 },
 *       { label: 'Last 30 days', count: 1234 }
 *     ]
 *   }
 * }
 */
export const getAsperoStorageStats = () => {
  return apiClient.get("/admin/aspero/aspero-storage-stats");
};
